<template>
  <div class="chat-list">
    <div class="search-container p-fluid">
      <span class="p-input-icon-left">
        <i class="pi pi-search" />
        <InputText v-model="searchQuery" placeholder="Buscar conversas" />
      </span>
    </div>
    
    <div v-if="loading" class="loading-chats">
      <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
      <p>Carregando conversas...</p>
    </div>
    
    <div v-else-if="filteredChats.length === 0" class="no-chats">
      <i class="pi pi-comments" style="font-size: 3rem; color: var(--text-color-secondary)"></i>
      <p v-if="searchQuery">Nenhuma conversa encontrada para "{{ searchQuery }}"</p>
      <p v-else>Nenhuma conversa disponível</p>
      <small>Inicie uma nova conversa usando o botão abaixo</small>
    </div>
    
    <div v-else class="chats-container">
      <div 
        v-for="chat in filteredChats" 
        :key="chat.id" 
        class="chat-item" 
        :class="{ 'active': selectedChatId === chat.id }"
        @click="selectChat(chat)"
      >
        <div class="avatar-container">
          <Avatar 
            :image="chat.profilePicture ? chat.profilePicture : null" 
            :label="getAvatarInitials(chat.name || chat.id)" 
            size="large" 
            shape="circle" 
            :style="{ backgroundColor: getAvatarColor(chat.id || '') }"
          />
          <span v-if="chat.unreadCount > 0" class="unread-badge">{{ chat.unreadCount }}</span>
        </div>
        
        <div class="chat-info">
          <div class="chat-header">
            <h3 class="chat-name">{{ chat.name || formatPhoneNumber(chat.id) }}</h3>
            <span class="chat-time">{{ formatLastMessageTime(chat.lastMessageTime) }}</span>
          </div>
          <div class="chat-preview">
            <p class="last-message" :class="{ 'unread': chat.unreadCount > 0 }">
              <span v-if="chat.lastMessage && chat.lastMessage.fromMe"
                    class="message-status-indicator"
              >
                <i :class="getStatusIcon(chat.lastMessage.status)"></i>
              </span>
              {{ getLastMessagePreview(chat) }}
            </p>
            <div class="chat-actions">
              <i v-if="chat.muted" class="pi pi-volume-off muted-icon"></i>
              <i v-if="chat.pinned" class="pi pi-thumbtack pinned-icon"></i>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <div class="new-chat-container">
      <Button 
        icon="pi pi-plus" 
        label="Nova Conversa" 
        class="p-button-primary new-chat-button" 
        @click="showNewChatDialog" 
      />
    </div>
    
    <Dialog 
      v-model:visible="newChatVisible" 
      header="Nova Conversa" 
      :modal="true" 
      :closable="true"
      :style="{width: '450px'}"
    >
      <div class="p-fluid">
        <div class="p-field">
          <label for="phoneNumber">Número de Telefone*</label>
          <InputMask 
            id="phoneNumber" 
            v-model="newChatForm.phoneNumber" 
            mask="(99) 99999-9999" 
            placeholder="(11) 99999-9999"
            :class="{ 'p-invalid': submitted && !newChatForm.phoneNumber }"
          />
          <small class="p-error" v-if="submitted && !newChatForm.phoneNumber">
            Número de telefone é obrigatório.
          </small>
          <small class="p-text-secondary">
            Digite o número de telefone no formato brasileiro com DDD
          </small>
        </div>
        
        <div class="p-field">
          <label for="contactName">Nome do Contato (opcional)</label>
          <InputText id="contactName" v-model="newChatForm.contactName" />
        </div>
        
        <div class="p-field">
          <label for="initialMessage">Mensagem Inicial (opcional)</label>
          <Textarea 
            id="initialMessage" 
            v-model="newChatForm.initialMessage" 
            rows="3" 
            autoResize 
          />
        </div>
      </div>
      
      <template #footer>
        <Button 
          label="Cancelar" 
          icon="pi pi-times" 
          class="p-button-text" 
          @click="newChatVisible = false" 
        />
        <Button 
          label="Iniciar Conversa" 
          icon="pi pi-check" 
          class="p-button-primary" 
          @click="createNewChat" 
          :loading="creatingChat" 
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue';

export default {
  name: 'ChatList',
  props: {
    instanceId: {
      type: [String, Number],
      required: true
    },
    chats: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    selectedChatId: {
      type: String,
      default: null
    }
  },
  emits: ['chat-selected', 'new-chat'],
  setup(props, { emit }) {
    const searchQuery = ref('');
    const newChatVisible = ref(false);
    const submitted = ref(false);
    const creatingChat = ref(false);
    
    const newChatForm = ref({
      phoneNumber: '',
      contactName: '',
      initialMessage: ''
    });
    
    const filteredChats = computed(() => {
      if (!searchQuery.value.trim()) {
        return props.chats;
      }
      
      const query = searchQuery.value.toLowerCase();
      return props.chats.filter(chat => {
        const name = (chat.name || '').toLowerCase();
        const phone = (chat.id || '').toLowerCase();
        return name.includes(query) || phone.includes(query);
      });
    });
    
    const formatPhoneNumber = (number) => {
      if (!number) return '';
      
      // Remove qualquer coisa que não seja número
      const cleaned = number.replace(/\D/g, '');
      
      // Formato brasileiro: (XX) XXXXX-XXXX
      if (cleaned.length === 11) {
        return `(${cleaned.substring(0, 2)}) ${cleaned.substring(2, 7)}-${cleaned.substring(7)}`;
      } else if (cleaned.length === 13) {
        return `+${cleaned.substring(0, 2)} (${cleaned.substring(2, 4)}) ${cleaned.substring(4, 9)}-${cleaned.substring(9)}`;
      }
      
      return number;
    };
    
    const getAvatarInitials = (name) => {
      if (!name) return '?';
      return name.split(' ')
        .map(word => word.charAt(0).toUpperCase())
        .slice(0, 2)
        .join('');
    };
    
    const getAvatarColor = (text) => {
      if (!text) return '#4caf50';
      
      let hash = 0;
      for (let i = 0; i < text.length; i++) {
        hash = text.charCodeAt(i) + ((hash << 5) - hash);
      }
      
      let color = '#';
      for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xFF;
        color += ('00' + value.toString(16)).substr(-2);
      }
      return color;
    };
    
    const formatLastMessageTime = (timestamp) => {
      if (!timestamp) return '';
      
      const date = new Date(timestamp);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      if (date >= today) {
        return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
      } else if (date >= yesterday) {
        return 'Ontem';
      } else {
        return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
      }
    };
    
    const getLastMessagePreview = (chat) => {
      if (!chat.lastMessage) return 'Iniciar conversa';
      
      // Verifica o tipo de mensagem
      if (chat.lastMessage.type === 'image') {
        return '📷 Foto';
      } else if (chat.lastMessage.type === 'video') {
        return '🎥 Vídeo';
      } else if (chat.lastMessage.type === 'audio') {
        return '🎵 Áudio';
      } else if (chat.lastMessage.type === 'document') {
        return '📎 Documento';
      } else if (chat.lastMessage.type === 'sticker') {
        return '🎭 Sticker';
      } else if (chat.lastMessage.type === 'location') {
        return '📍 Localização';
      } else if (chat.lastMessage.type === 'contact') {
        return '👤 Contato';
      }
      
      // Para mensagens de texto
      const content = chat.lastMessage.content || '';
      return content.length > 40 ? content.slice(0, 40) + '...' : content;
    };
    
    const getStatusIcon = (status) => {
      const statusIcons = {
        'pending': 'pi pi-clock',
        'sent': 'pi pi-check',
        'delivered': 'pi pi-check-circle',
        'read': 'pi pi-check-circle',
        'failed': 'pi pi-times'
      };
      
      return statusIcons[status] || 'pi pi-clock';
    };
    
    const selectChat = (chat) => {
      emit('chat-selected', chat);
    };
    
    const showNewChatDialog = () => {
      newChatForm.value.phoneNumber = '';
      newChatForm.value.contactName = '';
      newChatForm.value.initialMessage = '';
      submitted.value = false;
      newChatVisible.value = true;
    };
    
    const createNewChat = () => {
      submitted.value = true;
      
      if (!newChatForm.value.phoneNumber) {
        return;
      }
      
      creatingChat.value = true;
      
      // Formata o número para remover máscaras
      const phoneNumber = newChatForm.value.phoneNumber.replace(/\D/g, '');
      
      // Cria um objeto de nova conversa
      const newChat = {
        instanceId: props.instanceId,
        phoneNumber: phoneNumber,
        contactName: newChatForm.value.contactName,
        initialMessage: newChatForm.value.initialMessage
      };
      
      emit('new-chat', newChat);
      
      // Limpa o formulário e fecha o modal
      newChatForm.value.phoneNumber = '';
      newChatForm.value.contactName = '';
      newChatForm.value.initialMessage = '';
      submitted.value = false;
      creatingChat.value = false;
      newChatVisible.value = false;
    };
    
    return {
      searchQuery,
      filteredChats,
      newChatVisible,
      newChatForm,
      submitted,
      creatingChat,
      formatPhoneNumber,
      getAvatarInitials,
      getAvatarColor,
      formatLastMessageTime,
      getLastMessagePreview,
      getStatusIcon,
      selectChat,
      showNewChatDialog,
      createNewChat
    };
  }
};
</script>

<style scoped>
.chat-list {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: var(--ink-soft);
  border-right: 1px solid var(--line);
}

.search-container {
  padding: 1rem;
  border-bottom: 1px solid var(--line);
}

.chats-container {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem 0;
}

.chat-item {
  display: flex;
  padding: 0.75rem 1rem;
  gap: 0.75rem;
  transition: background-color 0.2s;
  border-bottom: 1px solid var(--line);
  cursor: pointer;
}

.chat-item:hover {
  background-color: var(--ink);
}

.chat-item.active {
  background-color: var(--ink);
  border-left: 2px solid var(--lime);
}

.avatar-container {
  position: relative;
}

.unread-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  background-color: var(--lime);
  color: var(--ink);
  border-radius: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: bold;
  border: 1px solid var(--text);
  font-family: 'IBM Plex Mono', ui-monospace, monospace;
}

.chat-info {
  flex: 1;
  min-width: 0; /* Para evitar overflow */
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.chat-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.25rem;
}

.chat-name {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--text);
  font-family: 'IBM Plex Sans', system-ui, sans-serif;
}

.chat-time {
  font-size: 0.75rem;
  color: var(--muted);
  white-space: nowrap;
  font-family: 'IBM Plex Mono', ui-monospace, monospace;
  font-weight: 500;
}

.chat-preview {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.last-message {
  margin: 0;
  font-size: 0.875rem;
  color: var(--muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 80%;
  font-family: 'IBM Plex Sans', system-ui, sans-serif;
}

.last-message.unread {
  font-weight: 600;
  color: var(--text);
}

.message-status-indicator {
  margin-right: 0.25rem;
  font-size: 0.75rem;
}

.chat-actions {
  display: flex;
  gap: 0.5rem;
}

.muted-icon, .pinned-icon {
  font-size: 0.875rem;
  color: var(--muted);
}

.new-chat-container {
  padding: 1rem;
  border-top: 1px solid var(--line);
}

.new-chat-button {
  width: 100%;
}

.loading-chats, .no-chats {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 2rem;
  color: var(--muted);
  text-align: center;
  gap: 1rem;
}

.p-text-secondary {
  color: var(--muted);
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
  font-family: 'IBM Plex Sans', system-ui, sans-serif;
}
</style> 